/** Single sign-on step-up: the window that confirms a privileged act by
 * signing in again at the organization's identity provider, and the page it
 * lands on. The server is the judge of success — the session's step-up time
 * moving forward — so a window the browser cut off from Starling (an
 * identity provider's opener policy does that) still completes; messages from
 * the landing page only make it quicker. */

const MESSAGE_TYPE = "starling.sso-stepup";
const CHANNEL = "starling-sso-stepup";

/** A sign-in window gets this long, matching the server's state lifetime. */
const STEP_UP_TIMEOUT_MS = 5 * 60 * 1000;

/** How often the session is read while the window is open. */
const POLL_MS = 3000;

/** The page the server sends a step-up window back to. */
export const SSO_STEP_UP_LANDING = "/auth/sso/stepup";
export const SSO_STEP_UP_WINDOW = "starling-sso-stepup";
export const SSO_STEP_UP_FEATURES = "popup,width=520,height=680";

export type SSOStepUpReason = "not_fresh" | "not_you" | "failed";

export interface SSOStepUpMessage {
  type: typeof MESSAGE_TYPE;
  result: "ok" | "error";
  reason?: SSOStepUpReason;
}

export const SSO_STEP_UP_FAILURES: Readonly<Record<SSOStepUpReason, string>> = {
  not_fresh:
    "Your identity provider did not confirm a fresh sign-in. Try again; if it keeps happening, ask an administrator to check that single sign-on reports the sign-in time.",
  not_you:
    "Your identity provider signed in a different account. Sign in there as yourself and try again.",
  failed: "Single sign-on did not finish. Try again.",
};

function isStepUpMessage(value: unknown): value is SSOStepUpMessage {
  if (typeof value !== "object" || value === null) return false;
  const record = value as Record<string, unknown>;
  return (
    record.type === MESSAGE_TYPE &&
    (record.result === "ok" || record.result === "error")
  );
}

function reasonOf(value: string | null): SSOStepUpReason {
  return value === "not_fresh" || value === "not_you" ? value : "failed";
}

/** Reads the landing page's address into the message it sends. */
export function stepUpMessageFrom(search: string): SSOStepUpMessage {
  const params = new URLSearchParams(search);
  return params.get("result") === "ok"
    ? { type: MESSAGE_TYPE, result: "ok" }
    : {
        type: MESSAGE_TYPE,
        result: "error",
        reason: reasonOf(params.get("reason")),
      };
}

/** Sent by the landing page to whichever window is waiting. */
export function announceSSOStepUp(message: SSOStepUpMessage): void {
  try {
    window.opener?.postMessage(message, window.location.origin);
  } catch {
    // The opener may already be gone; the channel below still carries it.
  }
  if (typeof BroadcastChannel === "function") {
    const channel = new BroadcastChannel(CHANNEL);
    channel.postMessage(message);
    channel.close();
  }
}

export interface SSOStepUpWait {
  /** Asks the server whether this session has been stepped up since the
   * attempt began. */
  confirmed: () => Promise<boolean>;
  signal?: AbortSignal;
}

/** The error a cancelled wait rejects with. */
export function isStepUpCancelled(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}

/** Waits for the session to be stepped up: resolves once the server says so,
 * rejects in words when the landing page reports a failure or the wait runs
 * out, and rejects as cancelled when `signal` aborts (closing the window). */
export function waitForSSOStepUp(
  popup: Window,
  { confirmed, signal }: SSOStepUpWait,
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      popup.close();
      reject(new DOMException("Cancelled", "AbortError"));
      return;
    }
    const channel =
      typeof BroadcastChannel === "function"
        ? new BroadcastChannel(CHANNEL)
        : null;
    let settled = false;
    let checking = false;
    const finish = (settle: () => void) => {
      if (settled) return;
      settled = true;
      window.removeEventListener("message", onWindowMessage);
      signal?.removeEventListener("abort", onAbort);
      channel?.close();
      window.clearInterval(poll);
      window.clearTimeout(deadline);
      settle();
    };
    const check = () => {
      if (checking || settled) return;
      checking = true;
      Promise.resolve()
        .then(confirmed)
        .then(
          (yes) => {
            checking = false;
            if (yes) finish(resolve);
          },
          () => {
            checking = false;
          },
        );
    };
    const take = (message: SSOStepUpMessage) => {
      if (message.result === "ok") {
        check();
        return;
      }
      finish(() =>
        reject(new Error(SSO_STEP_UP_FAILURES[message.reason ?? "failed"])),
      );
    };
    const onWindowMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (isStepUpMessage(event.data)) take(event.data);
    };
    const onAbort = () => {
      if (!popup.closed) popup.close();
      finish(() => reject(new DOMException("Cancelled", "AbortError")));
    };
    if (channel !== null) {
      channel.onmessage = (event: MessageEvent) => {
        if (isStepUpMessage(event.data)) take(event.data);
      };
    }
    const poll = window.setInterval(check, POLL_MS);
    const deadline = window.setTimeout(() => {
      if (!popup.closed) popup.close();
      finish(() =>
        reject(new Error("Single sign-on took too long. Try again.")),
      );
    }, STEP_UP_TIMEOUT_MS);
    window.addEventListener("message", onWindowMessage);
    signal?.addEventListener("abort", onAbort);
  });
}
